import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { toast } from "react-toastify";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import PhaseFormFields from "../PhaseFormFields";
import CustomFieldFormFields from "../CustomFieldFormFields";
import MovementRuleFormFields from "../MovementRuleFormFields";

// Tela de administracao do board novo (fases / campos personalizados /
// automacao) - ver plano "Kanban novo (estilo Pipefy)", Checkpoint 5.
// Cada linha salva/exclui direto na API (sem Formik/submit em lote) -
// mais simples de acompanhar visualmente numa lista curta e pouco
// frequente de editar (config de board, nao uso do dia a dia).
const BoardSettingsModal = ({ open, onClose, boardId, onChanged, onDeleted }) => {
  const [tab, setTab] = useState("board");
  const [loading, setLoading] = useState(false);
  const [phases, setPhases] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [movementRules, setMovementRules] = useState([]);
  const [tags, setTags] = useState([]);
  const [allBoards, setAllBoards] = useState([]);
  const [deleteConflict, setDeleteConflict] = useState(null);
  const [moveTargetPhaseId, setMoveTargetPhaseId] = useState("");

  // Renomear/excluir o board - pedido do usuario 2026-09-21 ("preciso
  // poder editar o nome e cancelar um board errado"), depois de eu
  // precisar apagar dois boards de teste direto no banco porque nao
  // existia jeito nenhum na tela pra isso (a API ja suportava, so'
  // faltava a UI).
  const [boardName, setBoardName] = useState("");
  const [savingName, setSavingName] = useState(false);
  // Pedido do usuario 2026-10-01: dado (anexo/campo personalizado) que
  // veio de outro board fica travado por padrao nesse board - esse
  // toggle libera a edicao pra quem realmente precisar corrigir em vez
  // de so' devolver o card (ver aba "Historico" do card).
  const [allowEditForeignData, setAllowEditForeignData] = useState(false);
  const [savingAllowEdit, setSavingAllowEdit] = useState(false);
  const [confirmDeleteBoardOpen, setConfirmDeleteBoardOpen] = useState(false);
  const [deletingBoard, setDeletingBoard] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const [boardRes, rulesRes, tagsRes, allBoardsRes] = await Promise.all([
        api.get(`/boards/${boardId}`),
        api.get("/movement-rules", { params: { boardId } }),
        api.get("/tags/list"),
        api.get("/boards"),
      ]);
      setBoardName(boardRes.data.name || "");
      setAllowEditForeignData(!!boardRes.data.allowEditForeignData);
      setPhases(boardRes.data.phases || []);
      setCustomFields(boardRes.data.customFieldDefinitions || []);
      setMovementRules(rulesRes.data || []);
      setTags(tagsRes.data || []);
      setAllBoards(allBoardsRes.data || []);
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  const handleSaveBoardName = async () => {
    if (!boardName.trim() || savingName) return;
    setSavingName(true);
    try {
      await api.put(`/boards/${boardId}`, { name: boardName.trim() });
      toast.success("Nome do board atualizado");
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingName(false);
    }
  };

  const handleToggleAllowEditForeignData = async (checked) => {
    setAllowEditForeignData(checked);
    setSavingAllowEdit(true);
    try {
      await api.put(`/boards/${boardId}`, { allowEditForeignData: checked });
    } catch (err) {
      toastError(err);
      setAllowEditForeignData(!checked);
    } finally {
      setSavingAllowEdit(false);
    }
  };

  const handleDeleteBoard = async () => {
    setDeletingBoard(true);
    try {
      await api.delete(`/boards/${boardId}`);
      toast.success("Board excluído");
      setConfirmDeleteBoardOpen(false);
      onClose();
      if (onDeleted) onDeleted();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingBoard(false);
    }
  };

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const handleSavePhase = async (phase, values) => {
    try {
      if (phase) {
        await api.put(`/phases/${phase.id}`, values);
      } else {
        await api.post("/phases", { ...values, boardId });
        toast.success("Fase criada!");
      }
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  // Reordenar fases (setinhas na lista) - pedido do usuario 2026-09-28
  // ("preciso poder mudar cada fase de posicao, exemplo pendencias tem
  // que ficar no ultimo"). O backend ja tinha isso pronto
  // (ReorderPhasesService / POST /phases/reorder) desde antes, so'
  // nunca tinha ganhado botao na tela - achado ao investigar o pedido.
  const handleMovePhase = async (phase, direction) => {
    const idx = phases.findIndex((p) => p.id === phase.id);
    if (idx === -1) return;
    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= phases.length) return;

    const reordered = [...phases];
    [reordered[idx], reordered[swapWith]] = [reordered[swapWith], reordered[idx]];
    setPhases(reordered);

    try {
      await api.post("/phases/reorder", {
        boardId,
        orderedPhaseIds: reordered.map((p) => p.id),
      });
    } catch (err) {
      toastError(err);
      fetchAll();
    }
  };

  const handleDeletePhase = async (phase) => {
    try {
      await api.delete(`/phases/${phase.id}`);
      toast.success("Fase excluída!");
      fetchAll();
    } catch (err) {
      if (err?.response?.data?.error === "ERR_PHASE_HAS_CARDS") {
        setDeleteConflict(phase);
        setMoveTargetPhaseId("");
      } else {
        toastError(err);
      }
    }
  };

  const handleConfirmMoveAndDelete = async () => {
    if (!deleteConflict || !moveTargetPhaseId) return;
    try {
      await api.delete(`/phases/${deleteConflict.id}`, {
        params: { moveCardsToPhaseId: moveTargetPhaseId },
      });
      toast.success("Cards movidos e fase excluída!");
      setDeleteConflict(null);
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveField = async (field, values) => {
    try {
      if (field) {
        await api.put(`/custom-fields/${field.id}`, values);
      } else {
        await api.post("/custom-fields", { ...values, boardId });
        toast.success("Campo criado!");
      }
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteField = async (field) => {
    try {
      await api.delete(`/custom-fields/${field.id}`);
      toast.success("Campo excluído!");
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveRule = async (rule, values) => {
    try {
      if (rule) {
        await api.put(`/movement-rules/${rule.id}`, values);
      } else {
        await api.post("/movement-rules", { ...values, boardId });
        toast.success("Regra criada!");
      }
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteRule = async (rule) => {
    try {
      await api.delete(`/movement-rules/${rule.id}`);
      toast.success("Regra excluída!");
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Configurações do board</DialogTitle>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
        <Tab label="Board" value="board" />
        <Tab label="Fases" value="phases" />
        <Tab label="Campos Personalizados" value="fields" />
        <Tab label="Automação" value="rules" />
      </Tabs>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {tab === "board" && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Nome do board
                </Typography>
                <Box display="flex" style={{ gap: 8 }} mb={3}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={boardName}
                    onChange={(e) => setBoardName(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={savingName || !boardName.trim()}
                    onClick={handleSaveBoardName}
                  >
                    Salvar
                  </Button>
                </Box>

                <Typography variant="subtitle2" gutterBottom>
                  Dados vindos de outro quadro
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
                  Quando um card chega neste board vindo de outro (ex: Comercial → Financeiro), os anexos e campos
                  personalizados de lá ficam travados por padrão (só leitura, na aba "Histórico" do card). Ligue
                  aqui se este board precisar poder corrigi-los diretamente, em vez de só devolver o card.
                </Typography>
                <Box mb={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowEditForeignData}
                        disabled={savingAllowEdit}
                        onChange={(e) => handleToggleAllowEditForeignData(e.target.checked)}
                      />
                    }
                    label="Autorizar edição de dados de outros quadros"
                  />
                </Box>

                <Typography variant="subtitle2" gutterBottom style={{ color: "#c62828" }}>
                  Excluir board
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
                  Remove o board e todas as fases, cards e campos personalizados dele. Não pode ser desfeito.
                </Typography>
                <Button
                  variant="outlined"
                  style={{ color: "#c62828", borderColor: "#c62828" }}
                  onClick={() => setConfirmDeleteBoardOpen(true)}
                >
                  Excluir este board
                </Button>
              </Box>
            )}

            {tab === "phases" && (
              <Box>
                {phases.map((phase, index) => (
                  <PhaseFormFields
                    key={phase.id}
                    phase={phase}
                    canMoveUp={index > 0}
                    canMoveDown={index < phases.length - 1}
                    onMoveUp={() => handleMovePhase(phase, "up")}
                    onMoveDown={() => handleMovePhase(phase, "down")}
                    onSave={(values) => handleSavePhase(phase, values)}
                    onDelete={() => handleDeletePhase(phase)}
                  />
                ))}
                <Typography variant="caption" color="textSecondary">
                  Nova fase:
                </Typography>
                <PhaseFormFields onSave={(values) => handleSavePhase(null, values)} />
              </Box>
            )}

            {tab === "fields" && (
              <Box>
                {customFields.map((field) => (
                  <CustomFieldFormFields
                    key={field.id}
                    field={field}
                    phases={phases}
                    onSave={(values) => handleSaveField(field, values)}
                    onDelete={() => handleDeleteField(field)}
                  />
                ))}
                <Typography variant="caption" color="textSecondary">
                  Novo campo:
                </Typography>
                <CustomFieldFormFields phases={phases} onSave={(values) => handleSaveField(null, values)} />
              </Box>
            )}

            {tab === "rules" && (
              <Box>
                {movementRules.map((rule) => (
                  <MovementRuleFormFields
                    key={rule.id}
                    rule={rule}
                    boardId={boardId}
                    boards={allBoards}
                    phases={phases}
                    tags={tags}
                    onSave={(values) => handleSaveRule(rule, values)}
                    onDelete={() => handleDeleteRule(rule)}
                  />
                ))}
                <Typography variant="caption" color="textSecondary">
                  Nova regra:
                </Typography>
                <MovementRuleFormFields
                  boardId={boardId}
                  boards={allBoards}
                  phases={phases}
                  tags={tags}
                  onSave={(values) => handleSaveRule(null, values)}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>

      <Dialog open={!!deleteConflict} onClose={() => setDeleteConflict(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mover cards antes de excluir</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            A fase "{deleteConflict?.name}" ainda tem cards. Escolha pra qual fase mover antes de excluir.
          </Typography>
          <TextField
            select
            label="Mover cards para"
            fullWidth
            margin="dense"
            variant="outlined"
            value={moveTargetPhaseId}
            onChange={(e) => setMoveTargetPhaseId(e.target.value)}
          >
            {phases
              .filter((p) => p.id !== deleteConflict?.id)
              .map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConflict(null)} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmMoveAndDelete}
            color="primary"
            variant="contained"
            disabled={!moveTargetPhaseId}
          >
            Mover e excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteBoardOpen} onClose={() => setConfirmDeleteBoardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir board "{boardName}"?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Isso remove o board, todas as fases e todos os cards dele. Não pode ser desfeito.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteBoardOpen(false)} color="secondary" disabled={deletingBoard}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteBoard}
            variant="contained"
            style={{ backgroundColor: "#c62828", color: "#fff" }}
            disabled={deletingBoard}
          >
            Excluir definitivamente
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BoardSettingsModal;
